// Componente para seleccionar y enviar imágenes (versión web).
// Usa input de archivo del navegador.
// Adaptado para web desde amarsi-app (React Native a React DOM).

import React, { useState, useCallback, useRef } from 'react';
import { FiImage, FiX } from 'react-icons/fi';

export default function ImageSelector({ onSendImage, onClose }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Maneja la selección de archivo
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Valida que sea una imagen
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona una imagen');
      return;
    }

    // Valida el tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen es muy grande. Máximo 5MB');
      return;
    }

    setSelectedImage(file);

    // Crea preview de la imagen
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  }, []);

  // Envía la imagen seleccionada
  const handleSendImage = useCallback(async () => {
    if (!selectedImage) return;

    setLoading(true);

    try {
      // Convierte la imagen a base64 para enviarla
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        
        if (onSendImage) {
          await onSendImage(base64);
        }
        
        onClose();
      };
      reader.readAsDataURL(selectedImage);
    } catch (error) {
      console.error('Error enviando imagen:', error);
      alert('Error al enviar la imagen');
    } finally {
      setLoading(false);
    }
  }, [selectedImage, onSendImage, onClose]);

  // Abre el selector de archivos
  const handleOpenFileSelector = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Limpia la selección
  const handleClearSelection = useCallback(() => {
    setSelectedImage(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  return (
    <div className="bg-gray-800 rounded-lg p-4 max-w-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white text-lg font-semibold">Enviar imagen</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <FiX size={20} />
        </button>
      </div>

      <div className="space-y-4">
        {/* Input de archivo oculto */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Preview de imagen */}
        {preview ? (
          <div className="relative">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-48 object-cover rounded-lg"
            />
            <button
              onClick={handleClearSelection}
              className="absolute top-2 right-2 bg-gray-900/80 hover:bg-gray-900 rounded-full p-2 transition-colors"
            >
              <FiX size={16} color="white" />
            </button>
          </div>
        ) : (
          <button
            onClick={handleOpenFileSelector}
            className="w-full bg-gray-700 hover:bg-gray-600 text-white p-6 rounded-lg flex flex-col items-center gap-2 transition-colors border-2 border-dashed border-gray-600"
          >
            <FiImage size={32} />
            <span className="text-sm">Seleccionar imagen</span>
          </button>
        )}

        {/* Botón de envío */}
        {preview && (
          <button
            onClick={handleSendImage}
            disabled={loading}
            className="w-full bg-primary hover:bg-primary/80 text-white p-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Enviando...' : 'Enviar imagen'}
          </button>
        )}

        <p className="text-gray-400 text-xs text-center">
          Máximo 5MB
        </p>
      </div>
    </div>
  );
}
